import { LauncherData } from './types';
import { handleApiResponse } from '../common';
import { getApiBaseUrl, API_VERSION } from '../index';

/**
 * Silo Launchers API - Manages Silo_Launcher_Record and Silo_Profile_Record entities
 * 
 * Naming Convention (see phase2_1_refactor_instructions.md):
 * - launchersApi = siloLaunchersApi (alias below)
 * - Maps to: Silo_Launcher_Record, Silo_Profile_Record
 * - Backend models: SiloLauncherModel, SiloProfileRecordModel
 */
export const launchersApi = {
  /**
   * List all launchers with optional profiles
   * @param {Object} options
   * @param {boolean} options.withProfiles - Include profile data
   * @param {number} options.spaceId - Filter by space ID (not yet implemented in backend)
   * @returns {Promise<{launchers: LauncherData[], total: number}>}
   */
  async list({ withProfiles = true, spaceId = null } = {}) {
    try {
      const params = new URLSearchParams();
      if (withProfiles) params.append('with_profiles', 'true');
      // Note: space_id filtering may be added to backend later
      
      const queryString = params.toString();
      const url = `${getApiBaseUrl()}${API_VERSION}/launchers${queryString ? '?' + queryString : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      
      const launchers = LauncherData.fromApiResponseList(data.launchers || []);
      
      return {
        launchers,
        total: data.total || launchers.length
      };
    } catch (error) {
      console.error('Failed to fetch launchers:', error);
      throw error;
    }
  },

  /**
   * Get a single launcher by ID
   */
  async get(launcherId) {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}${API_VERSION}/launchers/${launcherId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );
      
      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      
      return LauncherData.fromApiResponse(data.launcher);
    } catch (error) {
      console.error(`Failed to fetch launcher ${launcherId}:`, error);
      throw error;
    }
  },

  /**
   * Get launcher thumbnail as blob
   * Returns null if no thumbnail exists
   */
  async getThumbnail(launcherId) {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}${API_VERSION}/launchers/thumbnail/${launcherId}`,
        {
          credentials: 'include',
        }
      );
      
      if (response.status === 404 || response.status === 500) {
        return null;
      }
      
      const validatedResponse = await handleApiResponse(response);
      return validatedResponse.blob();
    } catch (error) {
      console.warn(`Thumbnail not available for launcher ${launcherId}`);
      return null;
    }
  },

  // =====================================================
  // LAUNCHER CRUD
  // =====================================================

  /**
   * Create a new launcher
   */
  async create(data) {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}${API_VERSION}/launchers`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(data)
        }
      );
      
      const validatedResponse = await handleApiResponse(response);
      const result = await validatedResponse.json();
      return result.launcher || result;
    } catch (error) {
      console.error('Failed to create launcher:', error);
      throw error;
    }
  },

  /**
   * Update a launcher
   */
  async update(launcherId, data) {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}${API_VERSION}/launchers/${launcherId}`,
        {
          method: 'PUT',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(data)
        }
      );
      
      const validatedResponse = await handleApiResponse(response);
      const result = await validatedResponse.json();
      return result.launcher || result;
    } catch (error) {
      console.error(`Failed to update launcher ${launcherId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a launcher
   */
  async delete(launcherId) {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}${API_VERSION}/launchers/${launcherId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );
      
      await handleApiResponse(response);
      return true;
    } catch (error) {
      console.error(`Failed to delete launcher ${launcherId}:`, error);
      throw error;
    }
  },

  /**
   * Upload launcher thumbnail
   */
  async uploadThumbnail(launcherId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Note: Backend route is /launchers/thumbnail/{id} not /launchers/{id}/thumbnail
      const response = await fetch(
        `${getApiBaseUrl()}${API_VERSION}/launchers/thumbnail/${launcherId}`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData
        }
      );
      
      const validatedResponse = await handleApiResponse(response);
      return validatedResponse.json();
    } catch (error) {
      console.error(`Failed to upload thumbnail for launcher ${launcherId}:`, error);
      throw error;
    }
  },

  // =====================================================
  // PROFILE CRUD
  // =====================================================

  /**
   * Create a profile for a launcher
   */
  async createProfile(launcherId, profileData) {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}${API_VERSION}/launchers/${launcherId}/profiles`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(profileData)
        }
      );
      
      const validatedResponse = await handleApiResponse(response);
      const result = await validatedResponse.json();
      return result.profile || result;
    } catch (error) {
      console.error(`Failed to create profile for launcher ${launcherId}:`, error);
      throw error;
    }
  },

  /**
   * Update a profile
   */
  async updateProfile(launcherId, profileId, profileData) {
    try {
      // Note: Backend route is /launchers/profiles/{profile_id} (PATCH, no launcher_id in path)
      const response = await fetch(
        `${getApiBaseUrl()}${API_VERSION}/launchers/profiles/${profileId}`,
        {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(profileData)
        }
      );
      
      const validatedResponse = await handleApiResponse(response);
      const result = await validatedResponse.json();
      return result.profile || result;
    } catch (error) {
      console.error(`Failed to update profile ${profileId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a profile
   */
  async deleteProfile(launcherId, profileId) {
    try {
      // Note: Backend route is /launchers/profiles/{profile_id} (no launcher_id in path)
      const response = await fetch(
        `${getApiBaseUrl()}${API_VERSION}/launchers/profiles/${profileId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );
      
      await handleApiResponse(response);
      return true;
    } catch (error) {
      console.error(`Failed to delete profile ${profileId}:`, error);
      throw error;
    }
  }
};

// Alias for new naming convention
export const siloLaunchersApi = launchersApi;

export default launchersApi;
